import { GridAdapter } from "./adapters/GridAdapter";
import {
  GridInstance,
  GridLayout,
  GridElementState,
  createGridInstanceId,
  createGridLayoutId,
  createDefaultLayouts,
  normalizeGridLayoutMinWidth,
  reassignLabels,
  MIN_LAYOUTS,
  MAX_LAYOUTS,
} from "./models/GridInstance";
import { scopeStringDevStorageKey } from "../core/StringDevStorageScope";

const STORAGE_PREFIX = "string-grid:";

function getStorageSlot(elementId: string): string {
  return scopeStringDevStorageKey(STORAGE_PREFIX + elementId);
}

function countSerializedGrids(data: unknown): number {
  if (!data || typeof data !== "object") {
    return 0;
  }

  if (Array.isArray(data)) {
    return data.length;
  }

  const object = data as Record<string, unknown>;
  const layouts = Array.isArray(object["layouts"]) ? object["layouts"] : [];
  let total = 0;

  for (const layout of layouts) {
    if (!layout || typeof layout !== "object") {
      continue;
    }

    const layoutObject = layout as Record<string, unknown>;
    const instances = Array.isArray(layoutObject["instances"])
      ? layoutObject["instances"]
      : Array.isArray(layoutObject["grids"])
        ? layoutObject["grids"]
        : [];
    total += instances.length;
  }

  return total;
}

/**
 * Centralized state manager for all grid instances across all elements.
 *
 * Responsibilities:
 * - Adapter registry (register / lookup)
 * - Per-element responsive layout CRUD (2–4 layouts)
 * - Per-layout grid instance CRUD
 * - Persistence to/from localStorage
 */
export class GridManager {
  private adapterRegistry: Map<string, GridAdapter> = new Map();
  private elementStates: Map<string, GridElementState> = new Map();

  registerAdapter(adapter: GridAdapter): void {
    this.adapterRegistry.set(adapter.type, adapter);
  }

  getAdapter(type: string): GridAdapter | undefined {
    return this.adapterRegistry.get(type);
  }

  getAdapterRegistry(): Map<string, GridAdapter> {
    return this.adapterRegistry;
  }

  getLayouts(elementId: string): GridLayout[] {
    return this.getOrInitState(elementId).layouts;
  }

  getLayoutCount(elementId: string): number {
    return this.getOrInitState(elementId).layouts.length;
  }

  getSelectedLayoutId(elementId: string): string {
    return this.getOrInitState(elementId).selectedLayoutId;
  }

  getPanelTitle(elementId: string): string {
    return this.getOrInitState(elementId).panelTitle?.trim() || "Layout";
  }

  getSelectedLayoutLabel(elementId: string): string {
    const state = this.getOrInitState(elementId);
    return this.findSelectedLayout(state)?.label ?? "S";
  }

  getResolvedLayoutId(elementId: string, viewportWidth: number): string {
    const state = this.getOrInitState(elementId);
    return this.findResolvedLayout(state, viewportWidth)?.id ?? state.layouts[state.layouts.length - 1].id;
  }

  getSelectedInstances(elementId: string): GridInstance[] {
    const state = this.getOrInitState(elementId);
    return this.findSelectedLayout(state)?.instances ?? [];
  }

  getResolvedInstances(elementId: string, viewportWidth: number): GridInstance[] {
    const state = this.getOrInitState(elementId);
    return this.findResolvedLayout(state, viewportWidth)?.instances ?? [];
  }

  /**
   * Adds a new layout breakpoint. Max 4. New layout is inserted before the
   * current smallest (S) and becomes the new XL / L / M depending on count.
   * Returns the new layout, or null if already at max.
   */
  addLayout(elementId: string): GridLayout | null {
    const state = this.getOrInitState(elementId);
    if (state.layouts.length >= MAX_LAYOUTS) return null;

    // Insert a new largest breakpoint at the front
    const currentLargest = state.layouts[0];
    const newMinWidth = currentLargest
      ? Math.min(1920, currentLargest.minWidth + 256)
      : 1280;

    const newLayout: GridLayout = {
      id: createGridLayoutId(),
      label: "XL", // will be reassigned below
      minWidth: newMinWidth,
      instances: [],
    };

    state.layouts.unshift(newLayout);
    this.sortLayouts(state);
    reassignLabels(state.layouts);
    this.ensureMinWidthConstraints(state);

    if (!state.layouts.some((l) => l.id === state.selectedLayoutId)) {
      state.selectedLayoutId = state.layouts[0].id;
    }

    this.save(elementId);
    return newLayout;
  }

  /**
   * Removes a layout by id. Min 2 layouts must remain.
   * Returns false if removal is not allowed.
   */
  removeLayout(elementId: string, layoutId: string): boolean {
    const state = this.getOrInitState(elementId);
    if (state.layouts.length <= MIN_LAYOUTS) return false;

    const index = state.layouts.findIndex((l) => l.id === layoutId);
    if (index === -1) return false;

    state.layouts.splice(index, 1);
    reassignLabels(state.layouts);
    this.ensureMinWidthConstraints(state);

    if (!state.layouts.some((l) => l.id === state.selectedLayoutId)) {
      state.selectedLayoutId = state.layouts[state.layouts.length - 1].id;
    }

    this.save(elementId);
    return true;
  }

  selectLayout(elementId: string, layoutId: string, persist: boolean = true): void {
    const state = this.getOrInitState(elementId);
    if (!state.layouts.some((layout) => layout.id === layoutId)) return;
    state.selectedLayoutId = layoutId;
    if (persist) this.save(elementId);
  }

  updateLayoutMinWidth(
    elementId: string,
    layoutId: string,
    minWidth: number,
    persist: boolean = true,
  ): void {
    const state = this.getOrInitState(elementId);
    const layout = state.layouts.find((l) => l.id === layoutId);
    if (!layout) return;

    layout.minWidth = normalizeGridLayoutMinWidth(minWidth);
    this.sortLayouts(state);
    reassignLabels(state.layouts);
    this.ensureMinWidthConstraints(state);
    if (persist) this.save(elementId);
  }

  replaceLayouts(
    elementId: string,
    layouts: GridLayout[],
    selectedLayoutId: string,
  ): void {
    const normalized = this.normalizeLayouts(layouts);
    const state: GridElementState = {
      elementId,
      layouts: normalized,
      selectedLayoutId,
    };
    this.ensureValidSelection(state);
    this.elementStates.set(elementId, state);
    this.save(elementId);
  }

  getInstances(elementId: string): GridInstance[] {
    return this.getSelectedInstances(elementId);
  }

  addInstance(elementId: string, type: string): GridInstance | null {
    const adapter = this.adapterRegistry.get(type);
    if (!adapter) return null;

    const layout = this.findSelectedLayout(this.getOrInitState(elementId));
    if (!layout) return null;

    const instance: GridInstance = {
      id: createGridInstanceId(),
      type,
      visible: true,
      settings: { ...adapter.getDefaults() },
    };

    layout.instances.push(instance);
    this.save(elementId);
    return instance;
  }

  removeInstance(elementId: string, instanceId: string): void {
    const layout = this.findSelectedLayout(this.getOrInitState(elementId));
    if (!layout) return;
    layout.instances = layout.instances.filter((i) => i.id !== instanceId);
    this.save(elementId);
  }

  reorderInstance(
    elementId: string,
    draggedId: string,
    targetId: string,
    placement: "before" | "after",
  ): void {
    const layout = this.findSelectedLayout(this.getOrInitState(elementId));
    if (!layout || draggedId === targetId) return;

    const fromIndex = layout.instances.findIndex((i) => i.id === draggedId);
    const targetIndex = layout.instances.findIndex((i) => i.id === targetId);
    if (fromIndex === -1 || targetIndex === -1) return;

    const [dragged] = layout.instances.splice(fromIndex, 1);
    const normalizedTarget = layout.instances.findIndex((i) => i.id === targetId);
    const insertIndex = placement === "before" ? normalizedTarget : normalizedTarget + 1;
    layout.instances.splice(insertIndex, 0, dragged);
    this.save(elementId);
  }

  moveInstanceToEnd(elementId: string, instanceId: string): void {
    const layout = this.findSelectedLayout(this.getOrInitState(elementId));
    if (!layout) return;

    const fromIndex = layout.instances.findIndex((i) => i.id === instanceId);
    if (fromIndex === -1 || fromIndex === layout.instances.length - 1) return;

    const [instance] = layout.instances.splice(fromIndex, 1);
    layout.instances.push(instance);
    this.save(elementId);
  }

  toggleVisibility(elementId: string, instanceId: string): void {
    const instance = this.findInstance(elementId, instanceId);
    if (!instance) return;
    instance.visible = !instance.visible;
    this.save(elementId);
  }

  renameInstance(elementId: string, instanceId: string, name: string): void {
    const instance = this.findInstance(elementId, instanceId);
    if (!instance) return;
    instance.name = name.trim() || undefined;
    this.save(elementId);
  }

  renamePanelTitle(elementId: string, title: string): void {
    const state = this.getOrInitState(elementId);
    state.panelTitle = title.trim() || "Layout";
    this.save(elementId);
  }

  updateSetting(elementId: string, instanceId: string, key: string, value: any): void {
    const instance = this.findInstance(elementId, instanceId);
    if (!instance) return;
    instance.settings[key] = value;
    this.save(elementId);
  }

  findInstance(elementId: string, instanceId: string): GridInstance | undefined {
    return this.getSelectedInstances(elementId).find((i) => i.id === instanceId);
  }

  save(elementId: string): void {
    const state = this.elementStates.get(elementId);
    if (!state) return;
    try {
      const payload = {
        layouts: state.layouts,
        selectedLayoutId: state.selectedLayoutId,
        panelTitle: state.panelTitle ?? "Layout",
      };
      const storageSlot = getStorageSlot(elementId);
      localStorage.setItem(storageSlot, JSON.stringify(payload));
    } catch (error) {
      console.warn("[StringDevLayout:storage] save failed", {
        elementId,
        storageSlot: getStorageSlot(elementId),
        error,
      });
    }
  }

  load(elementId: string, fallbackIds: string[] = []): void {
    try {
      const candidateIds = [elementId, ...fallbackIds];
      const candidates: Array<{
        elementId: string;
        storageSlot: string;
        raw: string;
        parsed: unknown;
        gridCount: number;
      }> = [];

      for (const candidateId of candidateIds) {
        const storageSlot = getStorageSlot(candidateId);
        const raw = localStorage.getItem(storageSlot);
        if (!raw) {
          continue;
        }

        try {
          const parsed = JSON.parse(raw);
          candidates.push({
            elementId: candidateId,
            storageSlot,
            raw,
            parsed,
            gridCount: countSerializedGrids(parsed),
          });
        } catch (error) {
          console.warn("[StringDevLayout:storage] candidate parse failed", {
            elementId,
            candidateId,
            storageSlot,
            error,
          });
        }
      }

      let selectedCandidate = candidates[0] ?? null;
      for (const candidate of candidates) {
        if (!selectedCandidate || candidate.gridCount > selectedCandidate.gridCount) {
          selectedCandidate = candidate;
        }
      }

      if (!selectedCandidate) return;

      const parsed = selectedCandidate.parsed;
      const resolvedStorageId = selectedCandidate.elementId;

      // Legacy: array of instances (pre-layouts era)
      if (Array.isArray(parsed)) {
        const valid = parsed.filter((i: any) => this.adapterRegistry.has(i.type));
        for (const i of valid) {
          const adapter = this.adapterRegistry.get(i.type);
          if (adapter) i.settings = { ...adapter.getDefaults(), ...i.settings };
        }
        const layouts = createDefaultLayouts(MIN_LAYOUTS);
        layouts[layouts.length - 1].instances = valid;
        this.elementStates.set(elementId, {
          elementId,
          layouts,
          selectedLayoutId: layouts[0].id,
          panelTitle: "Layout",
        });
        if (resolvedStorageId !== elementId) {
          this.save(elementId);
        }
        return;
      }

      const rawLayouts = Array.isArray(parsed?.layouts) ? parsed.layouts : null;
      if (!rawLayouts) return;

      const layouts = this.normalizeLayouts(rawLayouts);
      if (layouts.length === 0) return;

      const selectedLayoutId =
        typeof parsed?.selectedLayoutId === "string"
          ? parsed.selectedLayoutId
          : layouts[0].id;

      const state: GridElementState = {
        elementId,
        layouts,
        selectedLayoutId,
        panelTitle:
          typeof parsed?.panelTitle === "string" && parsed.panelTitle.trim().length > 0
            ? parsed.panelTitle.trim()
            : "Layout",
      };
      this.ensureValidSelection(state);
      this.elementStates.set(elementId, state);
      if (resolvedStorageId !== elementId) {
        this.save(elementId);
      }
    } catch {
      // ignore malformed storage data
    }
  }

  clear(): void {
    this.elementStates.clear();
  }

  // ── Private ───────────────────────────────────────────

  private getOrInitState(elementId: string): GridElementState {
    let state = this.elementStates.get(elementId);
    if (!state) {
      const layouts = createDefaultLayouts(MIN_LAYOUTS);
      state = { elementId, layouts, selectedLayoutId: layouts[0].id, panelTitle: "Layout" };
      this.elementStates.set(elementId, state);
    }
    this.sortLayouts(state);
    this.ensureValidSelection(state);
    return state;
  }

  private normalizeLayouts(layouts: any[]): GridLayout[] {
    const filtered = layouts.filter(
      (l: any) => l && typeof l === "object" && typeof l.id === "string",
    );

    const normalized: GridLayout[] = filtered.map((l: any) => {
      const instances = Array.isArray(l.instances)
        ? l.instances
        : Array.isArray(l.grids)
          ? l.grids
          : [];

      return {
        id: l.id,
        label: l.label ?? "S",
        minWidth: normalizeGridLayoutMinWidth(
          typeof l.minWidth === "number" ? l.minWidth : 0,
        ),
        instances: instances
          .filter((i: any) => this.adapterRegistry.has(i.type))
          .map((i: any) => {
            const adapter = this.adapterRegistry.get(i.type);
            return adapter
              ? { ...i, visible: i.visible !== false, settings: { ...adapter.getDefaults(), ...i.settings } }
              : i;
          }),
      } satisfies GridLayout;
    });

    // Clamp to allowed range
    const clamped = normalized.slice(0, MAX_LAYOUTS);
    while (clamped.length < MIN_LAYOUTS) {
      clamped.push({
        id: createGridLayoutId(),
        label: "S",
        minWidth: 0,
        instances: [],
      });
    }

    this.sortLayoutsArr(clamped);
    reassignLabels(clamped);
    this.ensureMinWidthConstraintsArr(clamped);
    return clamped;
  }

  private sortLayouts(state: GridElementState): void {
    this.sortLayoutsArr(state.layouts);
  }

  private sortLayoutsArr(layouts: GridLayout[]): void {
    layouts.sort((a, b) => b.minWidth - a.minWidth);
  }

  private ensureValidSelection(state: GridElementState): void {
    if (state.layouts.length < MIN_LAYOUTS) {
      state.layouts = createDefaultLayouts(MIN_LAYOUTS);
    }
    if (state.layouts.length > MAX_LAYOUTS) {
      state.layouts = state.layouts.slice(0, MAX_LAYOUTS);
      reassignLabels(state.layouts);
    }
    if (!state.layouts.some((l) => l.id === state.selectedLayoutId)) {
      state.selectedLayoutId = state.layouts[state.layouts.length - 1].id;
    }
  }

  private ensureMinWidthConstraints(state: GridElementState): void {
    this.ensureMinWidthConstraintsArr(state.layouts);
  }

  private ensureMinWidthConstraintsArr(layouts: GridLayout[]): void {
    // Smallest layout always has minWidth = 0
    if (layouts.length === 0) return;
    layouts[layouts.length - 1].minWidth = 0;

    // Each layout must have strictly smaller minWidth than the one above it
    for (let i = layouts.length - 2; i >= 0; i--) {
      const below = layouts[i + 1].minWidth;
      if (layouts[i].minWidth <= below) {
        layouts[i].minWidth = below + 1;
      }
    }
  }

  private findSelectedLayout(state: GridElementState): GridLayout | undefined {
    return state.layouts.find((l) => l.id === state.selectedLayoutId);
  }

  private findResolvedLayout(state: GridElementState, viewportWidth: number): GridLayout | undefined {
    for (const layout of state.layouts) {
      if (viewportWidth >= layout.minWidth) return layout;
    }
    return state.layouts[state.layouts.length - 1];
  }
}
